import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, ChevronDown, Pencil, Plus, Check, X, ArrowRight, Trash2, Copy, ArrowLeft, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { copyPart as clipCopy, getClipboardPart } from "@/utils/partClipboard";
import { formatINR } from "@/utils/formatINR";
import { computeNpd, computeAutoGrossWeight } from "@/utils/npdCalc";

const num = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? 0 : n; };

// Survives the breakdown remounting during a reload — so a just-added blank part opens in edit mode.
let _pendingAutoEditId = null;

// Per-part costing table. Inputs (pitch, width, weights) and each section TOTAL are their own
// columns with headers. Clicking a section total (Process, Treatment, Tooling, MOQ, O/H & Margin)
// opens ONLY that section's detail in its own dropdown under the row — not one huge combined dump.

const SECTION_TITLES = {
  A: "A · RM / Blank",
  B: "B · Process Cost",
  D: "D · Treatment & Maintenance",
  TOOL: "Tooling (one-time)",
  MOQ: "MOQ / Sourcing",
  E: "E · Overheads & Margin",
};

const BASIS_SHORT = {
  lump_sum: "lump ₹/pc",
  process_cost: "% of Process",
  gross_rmc: "% of Gross RMC",
  net_rmc: "% of Net RMC",
  sub_total: "% of Sub-Total",
};

const fmtNum = (v, d = 2) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(d);
const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}%`);
// Compact ₹ for the wide one-time columns (Tooling) so the whole table fits without scrolling.
// Full value is still shown in the section's dropdown.
const compactINR = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}k`;
  return formatINR(n);
};

// Shared column layout — header row and every data row use the SAME template so they line up.
// Kept tight so the whole table (incl. Grand Total) fits most screens without horizontal scroll;
// the PART column (fr) absorbs any slack. overflow-x-auto is only a fallback on very narrow screens.
const COLS =
  "minmax(150px,1fr) 72px 58px 46px 44px 50px 62px 56px 74px 74px 70px 78px 84px 98px";
const MINW = "min-w-[1040px]";

function Line({ label, value, sub, strong, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className={`text-xs ${strong ? "font-semibold text-slate-700" : "text-slate-500"}`}>
        {label}
        {sub ? <span className="text-slate-400 font-normal"> · {sub}</span> : null}
      </span>
      <span className={`font-mono text-xs whitespace-nowrap ${strong ? "font-semibold" : ""} ${accent || "text-slate-800"}`}>{value}</span>
    </div>
  );
}

const DINP = "w-24 px-1.5 py-0.5 border !border-red-500 rounded text-xs text-right font-mono bg-red-50 text-red-900 focus:outline-none focus:ring-1 focus:ring-red-500";

// Editable dropdown line — a labelled red input (used when a section's dropdown is edited inline).
function ELine({ label, value, onChange, sub, suffix, wide }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-xs text-slate-500">{label}{sub ? <span className="text-slate-400"> · {sub}</span> : null}</span>
      <span className="flex items-center gap-1">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${DINP} ${wide ? "w-40 text-left" : ""}`} />
        {suffix ? <span className="text-[10px] text-slate-400 w-4">{suffix}</span> : null}
      </span>
    </div>
  );
}

// A clickable section-total cell. Shows the total + a chevron (and an optional small % of grand
// total, like the costing live-calc panel); highlights when its dropdown is open.
function SecCell({ id, open, onToggle, value, pct, accent }) {
  const active = open === id;
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={`flex flex-col items-end justify-center px-1 h-full font-mono text-xs rounded transition leading-tight ${active ? "bg-indigo-100 text-indigo-700 font-semibold" : `hover:bg-indigo-50 ${accent || "text-slate-700"}`}`}
      data-testid={`sec-${id}`}
      title="Click to see this section's detail"
    >
      <span className="flex items-center gap-0.5">
        {value}
        <ChevronDown size={11} className={`flex-shrink-0 transition ${active ? "rotate-180" : "opacity-40"}`} />
      </span>
      {pct != null && <span className="text-[9px] font-semibold text-blue-800">{pct.toFixed(1)}%</span>}
    </button>
  );
}

const INP = "w-full px-1 py-0.5 border !border-red-500 rounded text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-red-500 bg-red-50 text-red-900";

function PartCard({ child, hideMargin, materials, operations, onSaved, onReload, autoEdit }) {
  const [open, setOpen] = useState(null);   // which section dropdown is open: A|B|D|TOOL|MOQ|E|null
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoStatus, setAutoStatus] = useState("idle");   // idle | saving | saved | error
  const toggle = (s) => setOpen((p) => (p === s ? null : s));
  const mb = child.moq_block || {};

  const matById = (id) => (materials || []).find((m) => m.material_id === id);
  const densityOf = (id) => { const m = matById(id); return m ? (Number(m.density_g_cc) || 7.85) : (Number(child.density_g_cc) || 7.85); };
  const recomputeGross = (d) => {
    if (d.gross_weight_overridden) return num(d.gross_weight);
    const base = { ...child, material_id: d.material_id, thickness_mm: num(d.thickness_mm), pitch: num(d.pitch), strip_width: num(d.strip_width), net_weight: num(d.net_weight) };
    const g = computeAutoGrossWeight(base, densityOf(d.material_id));
    return g > 0 ? g : num(d.gross_weight);
  };
  const _treatInit = (obj, legacy) => (obj ? { ...obj } : (legacy !== undefined && legacy !== null ? { mode: "per_piece", rate: legacy } : { mode: "per_piece", rate: 0 }));
  const startEdit = () => {
    setDraft({
      part_name: child.part_name || "", part_number: child.part_number || "",
      material_id: child.material_id || "", material_grade: child.material_grade || "",
      thickness_mm: child.thickness_mm ?? "", pitch: child.pitch ?? "", strip_width: child.strip_width ?? "",
      net_weight: child.net_weight ?? "", gross_weight: child.gross_weight ?? "", gross_weight_overridden: !!child.gross_weight_overridden,
      rm_price_per_kg: child.rm_price_per_kg ?? "", scrap_price_per_kg: child.scrap_price_per_kg ?? "",
      tooling_cost: child.tooling_cost ?? "", tooling_life: child.tooling_life ?? "",
      moq_kg: child.moq_kg ?? "", moq_confirmed_with: child.moq_confirmed_with ?? "", inventory_holding_pct: child.inventory_holding_pct ?? "",
      surface_treatment: _treatInit(child.surface_treatment, child.surface_treatment_cost),
      heat_treatment: _treatInit(child.heat_treatment, child.heat_treatment_cost),
      processes: (child.processes || []).map((p) => ({ ...p })),
      overheads: (child.overheads || []).map((o) => ({ ...o, _editpct: o.basis === "lump_sum" ? String(o.value ?? "") : String(+(((Number(o.value) || 0) * 100).toFixed(6))) })),
    });
    setEditing(true);
  };
  // A freshly-added blank part opens straight into edit mode so its row can be filled in.
  useEffect(() => { if (autoEdit && !editing) startEdit(); /* eslint-disable-next-line */ }, [autoEdit]);
  const setF = (k, v) => setDraft((prev) => { const d = { ...prev, [k]: v }; if (!d.gross_weight_overridden) d.gross_weight = recomputeGross(d); return d; });
  const setScalar = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));
  const setMaterial = (id) => setDraft((prev) => { const m = matById(id); const d = { ...prev, material_id: id, material_grade: m ? `${m.category} ${m.grade}`.trim() : prev.material_grade, rm_price_per_kg: m && (prev.rm_price_per_kg === "" || Number(prev.rm_price_per_kg) === 0) ? (m.default_rm_rate_per_kg ?? prev.rm_price_per_kg) : prev.rm_price_per_kg }; if (!d.gross_weight_overridden) d.gross_weight = recomputeGross(d); return d; });
  const setProc = (i, k, v) => setDraft((prev) => ({ ...prev, processes: prev.processes.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const removeProc = (i) => setDraft((prev) => ({ ...prev, processes: prev.processes.filter((_, idx) => idx !== i) }));
  // Change an existing step to a different operation (updates name + cost mode; keeps the entered rate).
  const setProcName = (i, name) => setDraft((prev) => ({ ...prev, processes: prev.processes.map((p, idx) => {
    if (idx !== i) return p;
    const op = (operations || []).find((o) => o.name === name);
    if (!op) return { ...p, name };
    return { ...p, name: op.name, cost_mode: op.default_cost_mode || p.cost_mode || "per_piece", tonnage: op.default_tonnage || p.tonnage, no_machine: !!op.no_machine };
  }) }));
  const addProcessByName = (name) => {
    const op = (operations || []).find((o) => o.name === name);
    if (!op) return;
    const mode = op.default_cost_mode || "per_piece";
    const dr = Number(op.default_rate) || 0;
    const step = { name: op.name, cost_mode: mode, rate: mode === "per_kg" ? dr : 0, cost: mode === "per_kg" ? 0 : dr, tonnage: op.default_tonnage || "", no_machine: !!op.no_machine, is_default: false };
    setDraft((prev) => ({ ...prev, processes: [...(prev.processes || []), step] }));
  };
  const setOh = (i, str) => setDraft((prev) => ({ ...prev, overheads: prev.overheads.map((o, idx) => idx === i ? { ...o, _editpct: str } : o) }));
  const ohValue = (o) => (o.basis === "lump_sum" ? num(o._editpct) : num(o._editpct) / 100);
  const setTreat = (kind, rate) => setDraft((prev) => ({ ...prev, [kind]: { ...(prev[kind] || { mode: "per_piece" }), rate } }));
  const buildPayload = (d) => ({
    part_name: d.part_name || "",
    part_number: d.part_number || "",
    material_id: d.material_id || null,
    material_grade: d.material_grade || null,
    thickness_mm: num(d.thickness_mm),
    pitch: num(d.pitch),
    strip_width: num(d.strip_width),
    net_weight: num(d.net_weight),
    gross_weight: num(d.gross_weight),
    gross_weight_overridden: !!d.gross_weight_overridden,
    rm_price_per_kg: num(d.rm_price_per_kg),
    tooling_cost: num(d.tooling_cost),
    tooling_life: num(d.tooling_life),
    moq_kg: num(d.moq_kg),
    moq_confirmed_with: d.moq_confirmed_with || null,
    inventory_holding_pct: num(d.inventory_holding_pct),
    surface_treatment: { ...d.surface_treatment, rate: num(d.surface_treatment.rate) },
    heat_treatment: { ...d.heat_treatment, rate: num(d.heat_treatment.rate) },
    processes: d.processes.map((p) => ({ ...p, rate: p.rate === "" || p.rate == null ? p.rate : num(p.rate), cost: p.cost === "" || p.cost == null ? p.cost : num(p.cost) })),
    overheads: d.overheads.map((o) => { const value = ohValue(o); const { _editpct, ...rest } = o; return { ...rest, value }; }),
  });
  // Persist the current draft. silent=true for auto-save (no toast/close).
  const persist = async (d, { silent } = {}) => {
    try {
      const r = await api.patch(`/npd/${child.npd_id}/inline-fields`, buildPayload(d));
      if (onSaved) onSaved(r.data);
      if (!silent) toast.success(`Saved — ${d.part_name || child.npd_number} updated`);
      const rev = r?.data?.reverted_assemblies || [];
      if (!silent && rev.length) {
        toast.warning(
          rev.length === 1 ? "Linked assembly sent for re-approval" : `${rev.length} linked assemblies sent for re-approval`,
          { description: `${rev.map((a) => a.assembly_number).join(", ")} moved to Under Approval because this child part changed.` }
        );
      }
      return true;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  };
  const save = async () => { setSaving(true); try { await persist(draft); setEditing(false); } finally { setSaving(false); } };
  const deletePart = async () => {
    if (!window.confirm(`Delete "${child.part_name || child.npd_number}"? It moves to Trash (admins can restore).`)) return;
    try {
      await api.delete(`/npd/${child.npd_id}`);
      toast.success("Part deleted");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  // Copy to the cross-page clipboard — then paste into any project with Ctrl+V (or the Paste button).
  const copyPart = () => {
    clipCopy("npd", child.npd_id, child.part_name || child.npd_number);
    toast.success(`Copied "${child.part_name || child.npd_number}" — open any project and press Ctrl+V to paste.`);
  };

  // Auto-save: 1s after you stop changing anything, persist to the part's costing (and the project).
  const draftSig = draft ? JSON.stringify(draft) : "";
  useEffect(() => {
    if (!editing || !draft) return undefined;
    setAutoStatus("saving");
    const t = setTimeout(async () => {
      const ok = await persist(draft, { silent: true });
      setAutoStatus(ok ? "saved" : "error");
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draftSig]);

  // Live totals while editing (so every section recomputes as you type); else use serialized.
  const liveNpd = editing && draft ? {
    ...child, material_id: draft.material_id,
    thickness_mm: num(draft.thickness_mm), pitch: num(draft.pitch), strip_width: num(draft.strip_width),
    net_weight: num(draft.net_weight), gross_weight: num(draft.gross_weight), gross_weight_overridden: !!draft.gross_weight_overridden,
    rm_price_per_kg: num(draft.rm_price_per_kg), scrap_price_per_kg: num(draft.scrap_price_per_kg || child.scrap_price_per_kg),
    tooling_cost: num(draft.tooling_cost), tooling_life: num(draft.tooling_life),
    moq_kg: num(draft.moq_kg), inventory_holding_pct: num(draft.inventory_holding_pct), moq_confirmed_with: draft.moq_confirmed_with,
    surface_treatment: draft.surface_treatment, heat_treatment: draft.heat_treatment,
    processes: draft.processes, overheads: draft.overheads.map((o) => ({ ...o, value: ohValue(o) })),
  } : null;
  const cv = liveNpd ? computeNpd(liveNpd) : (open ? computeNpd(child) : null);
  const c = open ? (liveNpd ? cv : computeNpd(child)) : null;
  const V = {
    net_rmc: cv ? cv.net_rmc : child.net_rmc,
    process_cost_total: cv ? cv.process_cost_total : child.process_cost_total,
    treatment_total: cv ? cv.treatment_total : child.treatment_total,
    total_development_cost: cv ? cv.total_development_cost : child.total_development_cost,
    overheads_and_margin: cv ? cv.overheads_and_margin : child.overheads_and_margin,
    margin_pct: cv ? cv.margin_pct : child.margin_pct,
    grand_total: cv ? cv.grand_total : child.grand_total,
  };
  const pctOf = (v) => (V.grand_total > 0 ? (Number(v) || 0) / V.grand_total * 100 : null);
  const gwDisplay = editing ? num(draft.gross_weight) : (Number(child.effective_gross_weight ?? child.gross_weight) || 0);

  return (
    <div className={`border-b border-slate-100 last:border-b-0 ${editing ? "border-l-2 border-l-indigo-400" : ""}`} data-testid={`part-breakdown-${child.npd_number}`}>
      <div className="grid items-center gap-x-1 px-3 py-2.5 [&>*]:border-r [&>*]:border-slate-100 [&>*]:pr-1 [&>*:last-child]:border-r-0" style={{ gridTemplateColumns: COLS }}>
        {/* PART — sticky-left so it stays visible while the middle columns scroll */}
        <div className="min-w-0 sticky left-0 z-10 bg-white pr-1.5">
          {editing ? (
            <>
              <div className="flex items-center gap-1">
                {child.child_index ? <span className="text-slate-400 font-mono text-[10px] flex-shrink-0">P{child.child_index}</span> : null}
                <input value={draft.part_name} onChange={(e) => setScalar("part_name", e.target.value)} placeholder="Part name" className="flex-1 min-w-0 px-1 py-0.5 border !border-red-500 rounded text-xs bg-red-50 text-red-900 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid={`edit-partname-${child.npd_number}`} />
                <span className="flex items-center gap-1 flex-shrink-0">
                  <button type="button" onClick={save} disabled={saving} className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40" title="Done (also auto-saves)" data-testid={`part-save-${child.npd_number}`}><Check size={15} /></button>
                  <button type="button" onClick={deletePart} className="text-slate-400 hover:text-red-600" title="Delete this part" data-testid={`part-delete-${child.npd_number}`}><Trash2 size={13} /></button>
                  <a href={`/npd/${child.npd_id}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-600" title="Open full costing page"><ArrowRight size={14} /></a>
                </span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <input value={draft.part_number} onChange={(e) => setScalar("part_number", e.target.value)} placeholder="Part no." className="flex-1 min-w-0 px-1 py-0.5 border !border-red-500 rounded text-[11px] font-mono bg-red-50 text-red-900 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid={`edit-partno-${child.npd_number}`} />
                <span className={`text-[10px] flex-shrink-0 ${autoStatus === "error" ? "text-red-600" : autoStatus === "saving" ? "text-amber-600" : "text-emerald-600"}`}>
                  {autoStatus === "saving" ? "Saving…" : autoStatus === "error" ? "Not saved" : autoStatus === "saved" ? "Saved ✓" : ""}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1">
                {child.child_index ? <span className="text-slate-400 font-mono text-xs">P{child.child_index}</span> : null}
                <span className="truncate">{child.part_name || "—"}</span>
                <button type="button" onClick={startEdit} className="text-slate-300 hover:text-orange-600 flex-shrink-0" title="Edit here — auto-saves to this part's costing (and the project)" data-testid={`part-edit-${child.npd_number}`}>
                  <Pencil size={12} />
                </button>
                <button type="button" onClick={copyPart} className="text-slate-300 hover:text-indigo-600 flex-shrink-0" title="Copy this part (duplicate)" data-testid={`part-copy-${child.npd_number}`}>
                  <Copy size={12} />
                </button>
                <button type="button" onClick={deletePart} className="text-slate-300 hover:text-red-600 flex-shrink-0" title="Delete this part" data-testid={`part-delete-${child.npd_number}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="text-[11px] text-slate-500 font-mono truncate">{child.part_number || child.npd_number}</div>
            </>
          )}
        </div>
        {/* Material + MOQ (MOQ sits right beside Material) */}
        {editing ? (
          <select value={draft.material_id} onChange={(e) => setMaterial(e.target.value)} className="w-full px-1 py-0.5 border !border-red-500 rounded text-xs bg-red-50 text-red-900 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid={`edit-rmtype-${child.npd_number}`}>
            <option value="">—</option>
            {(materials || []).map((m) => <option key={m.material_id} value={m.material_id}>{m.category} {m.grade}</option>)}
          </select>
        ) : (
          <div className="text-xs text-slate-700 truncate" title={child.material_grade || ""}>{child.material_grade || "—"}</div>
        )}
        <SecCell id="MOQ" open={open} onToggle={toggle} value={child.moq_kg ? `${fmtNum(child.moq_kg, 0)}kg` : "—"} />
        {/* Thickness */}
        {editing
          ? <input value={draft.thickness_mm} onChange={(e) => setF("thickness_mm", e.target.value)} className={INP} data-testid={`edit-thk-${child.npd_number}`} />
          : <div className="font-mono text-xs text-slate-600 text-right">{fmtNum(child.thickness_mm, 2)}</div>}
        {/* Input columns */}
        {editing
          ? <input value={draft.pitch} onChange={(e) => setF("pitch", e.target.value)} className={INP} />
          : <div className="font-mono text-xs text-slate-600 text-right">{fmtNum(child.pitch, 1)}</div>}
        {editing
          ? <input value={draft.strip_width} onChange={(e) => setF("strip_width", e.target.value)} className={INP} />
          : <div className="font-mono text-xs text-slate-600 text-right">{fmtNum(child.strip_width, 1)}</div>}
        {/* Gross weight is auto-calculated (pitch × width × thickness × density) — read-only, even in edit mode. */}
        <div className={`font-mono text-xs text-right ${editing ? "text-indigo-500 italic" : "text-slate-600"}`} title={editing ? "Auto-recalculated from pitch × width × thickness × density" : ""}>{fmtNum(gwDisplay, 4)}</div>
        {editing
          ? <input value={draft.net_weight} onChange={(e) => setF("net_weight", e.target.value)} className={INP} />
          : <div className="font-mono text-xs text-slate-600 text-right">{fmtNum(child.net_weight, 4)}</div>}
        {/* Section totals (clickable dropdowns; live values while editing). The small % is each
            section's share of the Grand Total — mirrors the costing live-calc panel. */}
        <SecCell id="A" open={open} onToggle={toggle} value={formatINR(V.net_rmc)} pct={pctOf(V.net_rmc)} />
        <SecCell id="B" open={open} onToggle={toggle} value={formatINR(V.process_cost_total)} pct={pctOf(V.process_cost_total)} />
        <SecCell id="D" open={open} onToggle={toggle} value={formatINR(V.treatment_total)} pct={pctOf(V.treatment_total)} />
        <SecCell id="TOOL" open={open} onToggle={toggle} value={compactINR(V.total_development_cost)} />
        {hideMargin ? <div /> : <SecCell id="E" open={open} onToggle={toggle} value={formatINR(V.overheads_and_margin)} pct={pctOf(V.overheads_and_margin)} />}
        {/* Grand Total (+ quote arrow) */}
        <div className="flex items-center justify-end gap-1.5">
          <span className="font-mono text-xs font-bold text-slate-900">{formatINR(V.grand_total)}</span>
          <a href={`/npd/${child.npd_id}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-800 flex-shrink-0" title="Open this part's costing page" data-testid={`part-quote-${child.npd_number}`}>
            <ArrowRight size={17} strokeWidth={3} />
          </a>
        </div>
      </div>

      {/* Per-section dropdown — only the clicked section's detail */}
      {open && c && (
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 max-w-2xl" data-testid={`sec-detail-${open}`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5 pb-1 border-b border-slate-200">{SECTION_TITLES[open]}</div>
          {open === "A" && (
            <>
              <Line label="Pitch (mm)" value={fmtNum(liveNpd ? liveNpd.pitch : child.pitch, 2)} />
              <Line label="Strip width (mm)" value={fmtNum(liveNpd ? liveNpd.strip_width : child.strip_width, 2)} />
              <Line label="Sheet width (mm)" value={fmtNum(child.sheet_width_mm, 0)} />
              <Line label="Thickness (mm)" value={fmtNum(liveNpd ? liveNpd.thickness_mm : child.thickness_mm, 2)} />
              <Line label="Gross weight (kg/pc)" value={fmtNum(gwDisplay, 4)} sub={Number(child.cavity_count) > 1 ? `${child.cavity_count}-cav` : null} />
              <Line label="Net weight (kg/pc)" value={fmtNum(liveNpd ? liveNpd.net_weight : child.net_weight, 4)} />
              {editing
                ? <ELine label="RM rate (₹/kg)" value={draft.rm_price_per_kg} onChange={(v) => setScalar("rm_price_per_kg", v)} suffix="₹" />
                : <Line label="RM rate (₹/kg)" value={formatINR(child.rm_price_per_kg)} />}
              <Line label="Gross RMC" value={formatINR(c.gross_rmc)} />
              <Line label="– Scrap recovery" value={`− ${formatINR(c.scrap_recovery)}`} accent="text-emerald-700" />
              <Line label="Net RMC (A)" value={formatINR(c.net_rmc)} strong />
            </>
          )}
          {open === "B" && (
            <>
              {editing ? (
                <>
                  {(draft.processes || []).map((p, i) => {
                    const perKg = (p.cost_mode || "per_piece") === "per_kg";
                    const known = (operations || []).some((o) => o.name === p.name);
                    return (
                      <div key={i} className="flex items-center gap-1.5 py-0.5">
                        {/* Process name is a dropdown so you can change which operation this step is */}
                        <select value={p.name || ""} onChange={(e) => setProcName(i, e.target.value)} className="flex-1 min-w-0 px-1 py-0.5 border !border-red-500 rounded text-xs bg-red-50 text-red-900 focus:outline-none focus:ring-1 focus:ring-red-500" data-testid={`proc-name-${child.npd_number}-${i}`}>
                          {(!known && p.name) ? <option value={p.name}>{p.name}</option> : null}
                          {(operations || []).map((op) => <option key={op.operation_id || op.name} value={op.name}>{op.name}</option>)}
                        </select>
                        <span className="text-[10px] text-slate-400 w-7 flex-shrink-0 text-right">{perKg ? "₹/kg" : "₹/pc"}</span>
                        <input value={(perKg ? p.rate : p.cost) ?? ""} onChange={(e) => setProc(i, perKg ? "rate" : "cost", e.target.value)} className="w-20 px-1.5 py-0.5 border !border-red-500 rounded text-xs text-right font-mono bg-red-50 text-red-900 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0" />
                        <button type="button" onClick={() => removeProc(i)} className="text-slate-300 hover:text-red-600 flex-shrink-0" title="Remove this process"><X size={13} /></button>
                      </div>
                    );
                  })}
                  {/* Process selector — add a step from the operations master */}
                  <div className="pt-1.5 mt-1 border-t border-slate-200">
                    <select value="" onChange={(e) => { if (e.target.value) { addProcessByName(e.target.value); e.target.value = ""; } }} className="w-full px-2 py-1 border border-orange-300 rounded text-xs text-orange-700 bg-orange-50 focus:outline-none focus:ring-1 focus:ring-orange-400" data-testid={`add-process-${child.npd_number}`}>
                      <option value="">+ Add process…</option>
                      {(operations || []).map((op) => <option key={op.operation_id || op.name} value={op.name}>{op.name}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                (c.processes_calculated || []).length === 0 ? <div className="text-[11px] text-slate-400">No process steps.</div> : (c.processes_calculated || []).map((p, i) => (
                  <Line key={i} label={p.name || `Step ${i + 1}`} sub={p.mode === "per_kg" ? `${formatINR(p.rate)}/kg × ${p.weight_basis} wt` : (p.tonnage || "per piece")} value={formatINR(p.amount)} />
                ))
              )}
              <Line label="Total Process Cost (B)" value={formatINR(c.process_cost_total)} strong />
            </>
          )}
          {open === "D" && (
            <>
              {editing
                ? <ELine label="Surface treatment" value={draft.surface_treatment?.rate} onChange={(v) => setTreat("surface_treatment", v)} suffix={draft.surface_treatment?.mode === "per_kg" ? "/kg" : "/pc"} />
                : <Line label="Surface treatment" value={formatINR(c.surface_cost)} />}
              {editing
                ? <ELine label="Heat treatment" value={draft.heat_treatment?.rate} onChange={(v) => setTreat("heat_treatment", v)} suffix={draft.heat_treatment?.mode === "per_kg" ? "/kg" : "/pc"} />
                : <Line label="Heat treatment" value={formatINR(c.heat_cost)} />}
              <Line label="Tool maintenance" value={formatINR(c.tool_maintenance)} sub={child.is_prototype ? "0 (prototype)" : "auto · % of B"} />
              <Line label="Total Treatment (D)" value={formatINR(c.treatment_total)} strong />
            </>
          )}
          {open === "TOOL" && (
            <>
              {editing
                ? <ELine label="Tooling cost" value={draft.tooling_cost} onChange={(v) => setScalar("tooling_cost", v)} suffix="₹" />
                : <Line label="Tooling cost" value={formatINR(c.tooling_cost_one_time)} />}
              {editing
                ? <ELine label="Tooling life (cycles)" value={draft.tooling_life} onChange={(v) => setScalar("tooling_life", v)} />
                : <Line label="Tooling life (cycles)" value={fmtNum(c.tooling_life_cycles, 0)} />}
              <Line label="Amortized in ₹/pc" value={c.amortize_tooling ? formatINR(c.tool_amortization) : "Off"} sub={c.amortize_tooling ? `${fmtNum(c.amortize_basis_cycles, 0)} cyc basis` : "charged separately"} />
              <Line label="Total Development Cost" value={formatINR(c.total_development_cost)} strong />
            </>
          )}
          {open === "MOQ" && (
            <>
              <Line label="Monthly RM requirement" value={`${fmtNum(child.monthly_rm_kg, 0)} kg/mo`} sub={child.monthly_rm_threshold_kg ? `threshold ${fmtNum(child.monthly_rm_threshold_kg, 0)} kg` : null} accent={child.low_volume_flag ? "text-amber-700" : "text-slate-800"} />
              {editing ? (
                <>
                  <ELine label="MOQ (kg)" value={draft.moq_kg} onChange={(v) => setScalar("moq_kg", v)} />
                  <ELine label="Confirmed with" value={draft.moq_confirmed_with} onChange={(v) => setScalar("moq_confirmed_with", v)} wide />
                  <ELine label="Inventory holding (%/yr)" value={draft.inventory_holding_pct} onChange={(v) => setScalar("inventory_holding_pct", v)} suffix="%" />
                  <Line label="Customer MOQ (pcs)" value={c.moq_block?.moq_batch_pcs ? Number(c.moq_block.moq_batch_pcs).toLocaleString("en-IN") : "—"} sub={c.moq_block?.months_of_cover != null ? `${fmtNum(c.moq_block.months_of_cover, 1)} mo cover` : null} />
                  <Line label="Per-piece carrying" value={c.moq_block?.per_piece_carrying_cost != null ? formatINR(c.moq_block.per_piece_carrying_cost) : "—"} />
                </>
              ) : ((child.moq_kg || mb.moq_kg) ? (
                <>
                  <Line label="MOQ (kg)" value={fmtNum(child.moq_kg || mb.moq_kg, 0)} sub={child.moq_confirmed_with ? `confirmed: ${child.moq_confirmed_with}` : "not confirmed"} />
                  <Line label="Customer MOQ (pcs)" value={mb.moq_batch_pcs ? Number(mb.moq_batch_pcs).toLocaleString("en-IN") : "—"} sub={mb.months_of_cover != null ? `${fmtNum(mb.months_of_cover, 1)} mo cover` : null} />
                  <Line label="Inventory holding (%/yr)" value={fmtPct(child.inventory_holding_pct)} />
                  <Line label="Per-piece carrying" value={mb.per_piece_carrying_cost != null ? formatINR(mb.per_piece_carrying_cost) : "—"} />
                </>
              ) : <div className="text-[11px] text-slate-400">No MOQ captured.</div>)}
            </>
          )}
          {open === "E" && !hideMargin && (
            <>
              {editing ? (
                (draft.overheads || []).length === 0 ? <div className="text-[11px] text-slate-400">No overhead rows.</div> : draft.overheads.map((o, i) => (
                  <ELine key={i} label={o.label || `Row ${i + 1}`} sub={o.basis === "lump_sum" ? "₹/pc" : (BASIS_SHORT[o.basis] || "%")} value={o._editpct} onChange={(v) => setOh(i, v)} suffix={o.basis === "lump_sum" ? "₹" : "%"} />
                ))
              ) : (
                (c.overheads_calculated || []).length === 0 ? <div className="text-[11px] text-slate-400">No overhead rows.</div> : (c.overheads_calculated || []).map((r, i) => (
                  <Line key={i} label={r.label || `Row ${i + 1}`} sub={r.basis === "lump_sum" ? `${formatINR(r.value)}/pc` : `${fmtNum((Number(r.value) || 0) * 100, 2)}% ${BASIS_SHORT[r.basis] ? "· " + BASIS_SHORT[r.basis] : ""}`} value={formatINR(r.calculated)} />
                ))
              )}
              <Line label="Total Overheads & Margin (E)" value={formatINR(c.overheads_and_margin)} strong />
              <Line label="Margin" value={`${formatINR(c.margin_amount)} · ${fmtPct(c.margin_pct)}`} strong accent={child.margin_red_flag ? "text-red-600" : "text-slate-800"} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProjectPartBreakdown({ parts: partsProp, hideMargin, onDownloadExcel, projectId, customerId, onReload }) {
  const parts = partsProp || [];
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addingPart, setAddingPart] = useState(false);
  const [addQty, setAddQty] = useState(1);                 // how many blank parts to add at once
  const [justAddedId, setJustAddedId] = useState(null);   // new part auto-opens in edit mode
  const [materials, setMaterials] = useState([]);
  const [operations, setOperations] = useState([]);
  const [overrides, setOverrides] = useState({});   // npd_id → fresh serialized row after an inline save

  // Materials list powers the RM Type dropdown + density; operations power the process picker.
  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
    api.get("/operations").then((r) => setOperations(r.data || [])).catch(() => {});
    // Pick up a just-added part (set before the reload that remounted this component).
    if (_pendingAutoEditId) { setJustAddedId(_pendingAutoEditId); _pendingAutoEditId = null; }
  }, []);

  // Paste the clipboard part into the selected project (shared by Ctrl+V and the Paste button).
  const pasteIntoProject = async () => {
    const clip = getClipboardPart();
    if (!clip) { toast.error("Nothing copied yet — click the Copy icon on a part first."); return; }
    if (!projectId) { toast.error("Pick a project first, then paste the copied part into it."); return; }
    try {
      const r = await api.post(`/projects/${projectId}/paste-part`, { kind: clip.kind || "npd", source_id: clip.id });
      setJustAddedId(r.data?.npd_id || null);
      _pendingAutoEditId = r.data?.npd_id || null;
      toast.success(`Pasted "${clip.label || "part"}" into this project.`);
      if (onReload) await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Paste failed");
    }
  };
  // Ctrl+V / Cmd+V pastes a copied part (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e) => {
      if (!((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V"))) return;
      const t = e.target;
      if (t && (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
      if (!getClipboardPart() || !projectId) return;
      e.preventDefault();
      pasteIntoProject();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, onReload]);

  // Add a blank part to THIS project, then reload so it appears as a row (auto-opened for editing).
  const addBlankPart = async () => {
    if (!projectId) return;
    const n = Math.max(1, Math.min(50, parseInt(addQty, 10) || 1));
    setAddingPart(true);
    try {
      let firstId = null;
      for (let k = 0; k < n; k++) {
        const r = await api.post(`/projects/${projectId}/parts`, { part_name: "", part_number: "", customer_id: customerId });
        if (!firstId) firstId = r.data?.npd_id || null;
      }
      setJustAddedId(firstId);
      _pendingAutoEditId = firstId;   // survive the remount that onReload triggers
      if (onReload) await onReload();
      toast.success(`${n} blank part${n > 1 ? "s" : ""} added — fill in the red boxes and Save.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add part");
    } finally {
      setAddingPart(false);
    }
  };

  // Render even with zero parts when we're in a project context, so the Add/Paste controls show.
  if (!parts.length && !projectId) return null;

  const download = async () => {
    if (!onDownloadExcel) return;
    setBusy(true);
    try { await onDownloadExcel(); } finally { setBusy(false); }
  };
  const handleSaved = (fresh) => { if (fresh?.npd_id) setOverrides((p) => ({ ...p, [fresh.npd_id]: fresh })); };

  return (
    <div id="part-cost-breakdown" className="bg-white border border-slate-200 rounded-lg p-4 mt-6 scroll-mt-4" data-testid="project-part-breakdown">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2">
          <FileText size={16} className="text-indigo-600" />
          Part Cost Breakdown ({parts.length})
        </h2>
        <div className="flex items-center gap-2">
          {/* Add shortcut — a new part (costing) OR a new project (with its parts) */}
          <div className="relative">
            {addOpen && <div className="fixed inset-0 z-20" onClick={() => setAddOpen(false)} />}
            <button type="button" onClick={() => setAddOpen((o) => !o)} className="px-3 py-1.5 text-xs border border-orange-300 text-orange-700 hover:bg-orange-50 rounded-md flex items-center gap-1.5 relative z-30" data-testid="breakdown-add" title="Add a new part or project">
              <Plus size={13} /> Add <ChevronDown size={12} className={`transition ${addOpen ? "rotate-180" : ""}`} />
            </button>
            {addOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-30 py-1" data-testid="breakdown-add-menu">
                <Link to="/npd/new" onClick={() => setAddOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-orange-50" data-testid="add-part">
                  <FileText size={13} className="text-orange-600" /> New Part (single costing)
                </Link>
                <Link to="/projects/new" onClick={() => setAddOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-orange-50 border-t border-slate-100" data-testid="add-project">
                  <FileText size={13} className="text-indigo-600" /> New Project (with its parts)
                </Link>
              </div>
            )}
          </div>
          {onDownloadExcel && (
            <button type="button" onClick={download} disabled={busy}
              className="px-3 py-1.5 text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-md flex items-center gap-1.5 disabled:opacity-50"
              data-testid="project-breakdown-excel" title="Download every part's full cost breakdown as Excel">
              <Download size={13} /> {busy ? "Preparing…" : "Download Excel"}
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Click a section total (A·RMC, Process, Treatment, Tooling, MOQ, O/H &amp; Margin) to open just that section&apos;s detail.
        Click the <Pencil size={11} className="inline -mt-0.5 text-slate-400" /> next to a part to edit RM Type, Thickness, Pitch, Width &amp; weights right here — totals recompute live and <span className="font-medium">save back to that part&apos;s costing</span> (and the project) automatically.
      </p>

      <div className="overflow-x-auto">
        <div className={MINW}>
          {/* Column header row — labels shown once in front of the parts */}
          <div className="grid items-center gap-x-1 px-3 py-2.5 rounded-t-md text-[11px] font-extrabold uppercase tracking-wider text-slate-700 bg-slate-100 border-b border-slate-200 [&>div]:border-r [&>div]:border-slate-300 [&>div]:pr-1 [&>div:last-child]:border-r-0" style={{ gridTemplateColumns: COLS }} data-testid="part-breakdown-header">
            <div className="sticky left-0 z-20 bg-slate-100 pr-1.5">Part</div>
            <div>Material</div>
            <div className="text-right">MOQ</div>
            <div className="text-right">Thk</div>
            <div className="text-right">Pitch</div>
            <div className="text-right">Strip W</div>
            <div className="text-right">Gross Wt</div>
            <div className="text-right">Net Wt</div>
            <div className="text-right">A·Net RMC</div>
            <div className="text-right">B·Process</div>
            <div className="text-right">D·Treat</div>
            <div className="text-right">Tooling</div>
            <div className="text-right">{hideMargin ? "" : "E·O/H & Mgn"}</div>
            <div className="text-right text-orange-600">Grand Total</div>
          </div>
          <div className="space-y-2 mt-2">
            {parts.length === 0 ? (
              <div className="text-center py-6 text-xs text-slate-400">No parts yet — add a blank part or paste a copied one below.</div>
            ) : parts.map((ch) => (
              <PartCard key={ch.npd_id} child={overrides[ch.npd_id] || ch} hideMargin={hideMargin} materials={materials} operations={operations} onSaved={handleSaved} onReload={onReload} autoEdit={justAddedId === ch.npd_id} />
            ))}
          </div>
        </div>
      </div>

      {/* Add new blank part(s) to this project (only on a single project's breakdown) */}
      {projectId && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-slate-500">Qty</span>
          <input type="number" min="1" max="50" value={addQty} onChange={(e) => setAddQty(e.target.value)} className="w-14 px-2 py-1.5 border border-orange-300 rounded-md text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-400" data-testid="breakdown-add-qty" />
          <button type="button" onClick={addBlankPart} disabled={addingPart}
            className="px-3 py-1.5 text-xs font-medium text-orange-700 border border-orange-300 rounded-md hover:bg-orange-50 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="breakdown-add-part-row" title="Add blank part(s) to this project">
            <Plus size={14} /> {addingPart ? "Adding…" : `Add ${(parseInt(addQty, 10) || 1) > 1 ? `${parseInt(addQty, 10)} parts` : "part"}`}
          </button>
          <button type="button" onClick={pasteIntoProject}
            className="px-3 py-1.5 text-xs font-medium text-indigo-700 border border-indigo-300 rounded-md hover:bg-indigo-50 flex items-center gap-1.5"
            data-testid="breakdown-paste-part" title="Paste a copied part into this project (or press Ctrl+V)">
            <ClipboardPaste size={14} /> Paste part
          </button>
        </div>
      )}
    </div>
  );
}
